import type { IntelTechnologyListItemDto, StudyTaskSelectDto, StudyTaskTechnologySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { EntityPickerPopover } from '@components/shared/entity-picker-popover.component';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useIntelTechnologiesQuery } from '@queries/intel-technologies.query';
import {
  useCreateMeiaTaskTechnology,
  useDeleteMeiaTaskTechnology,
  useGetMeiaTaskAttachableTechnologies,
  useGetMeiaTaskTechnologies,
} from '@queries/meia.query';
import { ExternalLinkIcon, Trash2Icon } from 'lucide-react';
import { type FC, useState } from 'react';

const TASK_TECHNOLOGIES_TOOLTIP =
  'Technologies attached to this Task represent the components or systems engaged by the Task. They drive downstream P4 component coverage.';

type MeiaTaskTechnologiesStepProps = {
  draftTechnologies: IntelTechnologyListItemDto[];
  readOnly: boolean;
  setDraftTechnologies: (technologies: IntelTechnologyListItemDto[]) => void;
  studyId: string;
  task?: StudyTaskSelectDto | null;
};

export const MeiaTaskTechnologiesStep: FC<MeiaTaskTechnologiesStepProps> = (props) => {
  const { draftTechnologies, readOnly, setDraftTechnologies, studyId, task } = props;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data: taskTechnologies = [] } = useGetMeiaTaskTechnologies({ studyId, taskId: task?.id });
  const { data: attachableTechnologiesData, isLoading: isLoadingAttachableTechnologies } =
    useGetMeiaTaskAttachableTechnologies({
      enabled: Boolean(task),
      limit: 20,
      page: 1,
      search: debouncedSearch,
      studyId,
      taskId: task?.id,
    });
  const { data: draftTechnologiesData, isLoading: isLoadingDraftTechnologies } = useIntelTechnologiesQuery({
    enabled: Boolean(!task),
    limit: 20,
    page: 1,
    search: debouncedSearch,
  });
  const { mutateAsync: createTaskTechnology } = useCreateMeiaTaskTechnology();
  const { mutateAsync: deleteTaskTechnology, isPending: isDeletingTaskTechnology } = useDeleteMeiaTaskTechnology();

  const excludedDraftTechnologyIds = new Set(draftTechnologies.map((technology) => technology.id));
  const technologies = task
    ? (attachableTechnologiesData?.technologies ?? [])
    : (draftTechnologiesData?.technologies.filter((technology) => !excludedDraftTechnologyIds.has(technology.id)) ??
      []);
  const isLoadingTechnologies = task ? isLoadingAttachableTechnologies : isLoadingDraftTechnologies;
  const displayedTechnologies = taskTechnologies.length > 0 || task ? taskTechnologies : draftTechnologies;
  const hasTechnologies = displayedTechnologies.length > 0;

  const handleAddTechnology = async (technology: IntelTechnologyListItemDto): Promise<void> => {
    if (readOnly) return;

    if (!task) {
      setDraftTechnologies([...draftTechnologies, technology]);
      return;
    }

    try {
      await createTaskTechnology({
        function: technology.name,
        studyId,
        taskId: task.id,
        technologyId: technology.id,
      });
      successToast({ description: 'Technology attached.' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to attach technology.';
      errorToast({ description: message });
    }
  };

  const handleRemoveDraftTechnology = (technology: IntelTechnologyListItemDto): void => {
    if (readOnly) return;

    setDraftTechnologies(draftTechnologies.filter((item) => item.id !== technology.id));
  };

  const handleRemoveTechnology = async (taskTechnology: StudyTaskTechnologySelectDto): Promise<void> => {
    if (readOnly) return;
    if (!task || !taskTechnology.id) return;
    try {
      await deleteTaskTechnology({
        studyId,
        taskId: task.id,
        taskTechnologyId: taskTechnology.id,
      });
      successToast({ description: 'Technology removed.' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove technology.';
      errorToast({ description: message });
    }
  };

  const renderTechnologyOption = (technology: IntelTechnologyListItemDto): JSX.Element => (
    <>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{technology.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {technology.companyName} · {technology.categoryName}
        </p>
      </div>
      <span className="shrink-0 rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">
        {technology.trlScore === undefined ? 'TRL -' : `TRL ${technology.trlScore}`}
      </span>
    </>
  );

  const picker = !readOnly && (
    <EntityPickerPopover<IntelTechnologyListItemDto>
      addButtonLabel="Add Technology"
      emptyMessage="No available technologies match this search."
      isLoading={isLoadingTechnologies}
      onAdd={handleAddTechnology}
      onSearchChange={setDebouncedSearch}
      options={technologies}
      placeholder="Search technologies"
      renderOption={renderTechnologyOption}
      search={debouncedSearch}
    />
  );

  return (
    <section className="flex flex-col gap-4">
      {hasTechnologies && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
            Engaged Technologies
            <TooltipComponent>{TASK_TECHNOLOGIES_TOOLTIP}</TooltipComponent>
          </div>
          {picker}
        </div>
      )}

      {hasTechnologies ? (
        <div className="flex flex-col gap-2">
          {task
            ? taskTechnologies.map((taskTechnology) => (
                <MeiaTaskTechnologyCard
                  isRemoving={isDeletingTaskTechnology}
                  key={taskTechnology.id}
                  onRemove={handleRemoveTechnology}
                  readOnly={readOnly}
                  taskTechnology={taskTechnology}
                />
              ))
            : draftTechnologies.map((technology) => (
                <MeiaDraftTaskTechnologyCard
                  key={technology.id}
                  onRemove={handleRemoveDraftTechnology}
                  readOnly={readOnly}
                  technology={technology}
                />
              ))}
        </div>
      ) : (
        <EmptyStateContainer
          action={picker || undefined}
          className="min-h-[260px] rounded-md border border-dashed border-border text-center"
          title="No Engaged Technologies"
        />
      )}
    </section>
  );
};

type MeiaDraftTaskTechnologyCardProps = {
  onRemove: (technology: IntelTechnologyListItemDto) => void;
  readOnly: boolean;
  technology: IntelTechnologyListItemDto;
};

const MeiaDraftTaskTechnologyCard: FC<MeiaDraftTaskTechnologyCardProps> = (props) => {
  const { onRemove, readOnly, technology } = props;
  const technologyUrl = `/technology/${technology.id}`;

  return (
    <Tile
      className="rounded-md border border-border p-3 shadow-none"
      contentClassName="gap-2"
      hoverReveal
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Score
            className="shrink-0"
            score={technology.trlScore}
            size="xs"
            type="TRL"
          />
          <div className="min-w-0">
            <a
              className="group/title inline-flex max-w-full items-center gap-1 font-medium text-gray-900 hover:underline"
              href={technologyUrl}
              rel="noreferrer"
              target="_blank"
            >
              <span className="truncate">{technology.name}</span>
              <ExternalLinkIcon className="hidden size-3.5 shrink-0 text-muted-foreground group-hover/title:block" />
            </a>
            <p className="mt-1 truncate text-xs text-muted-foreground">{technology.companyName}</p>
          </div>
        </div>
        {!readOnly && (
          <IconButton.HoverReveal
            ariaLabel={`Remove ${technology.name}`}
            icon={<Trash2Icon />}
            onClick={() => onRemove(technology)}
            size="sm"
            variant="destructive"
          />
        )}
      </div>
      <div className="flex flex-wrap gap-2 pl-11">
        <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">{technology.categoryName}</span>
      </div>
    </Tile>
  );
};

type MeiaTaskTechnologyCardProps = {
  isRemoving: boolean;
  onRemove: (taskTechnology: StudyTaskTechnologySelectDto) => Promise<void>;
  readOnly: boolean;
  taskTechnology: StudyTaskTechnologySelectDto;
};

const MeiaTaskTechnologyCard: FC<MeiaTaskTechnologyCardProps> = (props) => {
  const { isRemoving, onRemove, readOnly, taskTechnology } = props;
  const technologyName = taskTechnology.technologyName ?? taskTechnology.technologyId;
  const technologyUrl = `/technology/${taskTechnology.technologyId}`;

  return (
    <Tile
      className="rounded-md border border-border p-3 shadow-none"
      contentClassName="gap-2"
      hoverReveal
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Score
            className="shrink-0"
            score={taskTechnology.trlSnapshot}
            size="xs"
            type="TRL"
          />
          <div className="min-w-0">
            <a
              className="group/title inline-flex max-w-full items-center gap-1 font-medium text-gray-900 hover:underline"
              href={technologyUrl}
              rel="noreferrer"
              target="_blank"
            >
              <span className="truncate">{technologyName}</span>
              <ExternalLinkIcon className="hidden size-3.5 shrink-0 text-muted-foreground group-hover/title:block" />
            </a>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {taskTechnology.technologyCompanyName ?? 'Unknown organization'}
            </p>
          </div>
        </div>
        {!readOnly && (
          <IconButton.HoverReveal
            ariaLabel={`Remove ${technologyName}`}
            disabled={isRemoving}
            icon={<Trash2Icon />}
            onClick={() => void onRemove(taskTechnology)}
            size="sm"
            variant="destructive"
          />
        )}
      </div>
      <div className="flex flex-wrap gap-2 pl-11">
        <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
          {taskTechnology.technologyCategoryName ?? 'Uncategorized'}
        </span>
      </div>
    </Tile>
  );
};
